import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, InputAdornment, Autocomplete, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { wildSearchPhysicians } from '../api';

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#fff',
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const RoastButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1a1a2e',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#16213e',
  },
  borderRadius: '4px',
  padding: '8px 16px',
  marginLeft: theme.spacing(1),
}));

const SearchForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: '500px',
  marginTop: theme.spacing(2),
}));

const PhysicianSearch = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  // Fetch physician suggestions when input changes
  useEffect(() => {
    let active = true;
    if (inputValue === '') {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    wildSearchPhysicians(inputValue).then((results) => {
      if (active) {
        setOptions(results);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [inputValue]);

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedPhysician && onSearch) {
      onSearch(selectedPhysician.npi);
    } else if (searchQuery.trim() && onSearch) {
      onSearch(searchQuery);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fcfd', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      {/* Header Section */}
      <Box sx={{ pt: 8, pb: 2, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          Know My Doctor
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          Your Doctor's Résumé – Minus the Boring Parts
          {/* <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="9" cy="9" r="9" fill="#E0E7FF"/><text x="9" y="13" textAnchor="middle" fontSize="12" fill="#6366F1" fontFamily="Arial">i</text></svg>
          </Box> */}
        </Typography>
      </Box>

      {/* Search Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <form onSubmit={handleSearch} style={{ width: '100%', maxWidth: 500 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Autocomplete
              freeSolo
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.full_name)}
              options={options}
              loading={loading}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              onChange={(event, value) => setSelectedPhysician(value)}
              renderInput={(params) => (
                <SearchField
                  {...params}
                  placeholder="Enter Physician Name"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {loading ? <CircularProgress color="inherit" size={20} /> : <SearchIcon />}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />
            <RoastButton
              type="submit"
              variant="contained"
              sx={{
                ml: 2,
                width: 120,
                height: '56px',
                mt: 0,
                boxShadow: 'none',
                backgroundColor: '#1EB0BC',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#1EB0BC',
                },
              }}
            >
              Roast
            </RoastButton>
          </Box>
        </form>
      </Box>

      {/* Feature Cards Section */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3, mt: 6, mb: 6 }}>
        <Box sx={{ bgcolor: '#fff', boxShadow: 2, borderRadius: 2, p: 3, minWidth: 280, maxWidth: 340 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            AI-Powered Roast
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Because even doctors deserve a little fun. Try our lighthearted résumé roast.
          </Typography>
        </Box>
        <Box sx={{ bgcolor: '#fff', boxShadow: 2, borderRadius: 2, p: 3, minWidth: 280, maxWidth: 340 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Clinical Capability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Get a glimpse of your doctor's medical expertise and the way s/he thinks, treats, and communicates with patients.
          </Typography>
        </Box>
        <Box sx={{ bgcolor: '#fff', boxShadow: 2, borderRadius: 2, p: 3, minWidth: 280, maxWidth: 340 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Care with Compatibility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See how well your care values align with the doctor's style of practice and personality.
          </Typography>
        </Box>
      </Box>

      {/* Supported By Logos Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Supported by
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap', mb: 2 }}>
          <img src="https://dehayf5mhw1h7.cloudfront.net/wp-content/uploads/sites/1820/2023/02/20153207/Johns-Hopkins-Logo.png" alt="AWS" style={{ height: 48 }} />
          <img src="https://www.spartacommodities.com/wp-content/uploads/2024/04/NV_Inception_Program_Logo_NV_Inception_Logo_H_CMYK-1080x662.png.webp" alt="AWS" style={{ height: 48 }} />
          <img src="https://www.scra.org/wp-content/uploads/2024/02/EozAmF_XcAI8iCh.jpg" alt="AWS" style={{ height: 48 }} />
          <img src="https://venturewell.org/wp-content/themes/venturewell2016/inc/img/icorps_shaded.png" alt="AWS" style={{ height: 48 }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
        © 2025 LINKMedicine.AI. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default PhysicianSearch;
